/**
 * Client-side error reporting and session tracking utilities
 * Used to send error logs and session diagnostics to the admin dashboard
 */

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Detect user's platform
 */
export const detectPlatform = () => {
  const ua = navigator.userAgent.toLowerCase();
  
  if (/iphone|ipad|ipod/.test(ua)) {
    return 'ios';
  } else if (/android/.test(ua)) {
    return 'android';
  } else if (/windows|macintosh|linux/.test(ua)) {
    return 'desktop';
  }
  return 'unknown';
};

/**
 * Detect user's browser
 */
export const detectBrowser = () => {
  const ua = navigator.userAgent.toLowerCase();
  
  if (/safari/.test(ua) && !/chrome/.test(ua)) {
    return 'safari';
  } else if (/chrome/.test(ua) && !/edge/.test(ua)) {
    return 'chrome';
  } else if (/firefox/.test(ua)) {
    return 'firefox';
  } else if (/edge/.test(ua)) {
    return 'edge';
  }
  return 'unknown';
};

/**
 * Report an error to the backend
 * @param {string} errorType - Type of error: stt, tts, stripe, booking, auth, translation, ui
 * @param {string} message - Error message
 * @param {object} options - Additional options
 */
export const reportError = async (errorType, message, options = {}) => {
  try {
    const token = localStorage.getItem('passportTalk_token');
    if (!token) return; // Don't report if not logged in
    
    const language = localStorage.getItem('passportTalk_primaryLanguage') || 'en';
    
    const payload = {
      error_type: errorType,
      message: message,
      platform: detectPlatform(),
      browser: detectBrowser(),
      language: language,
      stack_trace: options.stackTrace || null,
      component: options.component || null,
      url: window.location.href
    };
    
    await fetch(`${API_BASE_URL}/api/client/error`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(payload)
    });
    
    // Also log to console in development
    if (process.env.NODE_ENV === 'development') {
      console.log('[ErrorReport]', errorType, message);
    }
  } catch (e) {
    // Silently fail - don't want error reporting to cause more errors
    console.warn('Failed to report error:', e);
  }
};

/**
 * Update session info on the server
 * Called on app load or when significant actions happen
 */
export const updateSession = async (sessionData = {}) => {
  try {
    const token = localStorage.getItem('passportTalk_token');
    if (!token) return;
    
    const language = localStorage.getItem('passportTalk_primaryLanguage') || 'en';
    
    const payload = {
      platform: detectPlatform(),
      browser: detectBrowser(),
      language: language,
      ...sessionData
    };
    
    await fetch(`${API_BASE_URL}/api/client/session`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(payload)
    });
  } catch (e) {
    console.warn('Failed to update session:', e);
  }
};

/**
 * Track voice input attempt
 */
export const trackVoiceAttempt = async () => {
  await updateSession({ voice_attempted: true });
};

/**
 * Track STT usage
 */
export const trackSTTUsage = async () => {
  await updateSession({ stt_used: true });
};

/**
 * Track TTS usage
 */
export const trackTTSUsage = async () => {
  await updateSession({ tts_used: true });
};

/**
 * Report STT error
 */
export const reportSTTError = async (errorMessage, component = null) => {
  await reportError('stt', errorMessage, { component });
};

/**
 * Report TTS error
 */
export const reportTTSError = async (errorMessage, component = null) => {
  await reportError('tts', errorMessage, { component });
};

/**
 * Report Stripe/payment error
 */
export const reportStripeError = async (errorMessage, component = null) => {
  await reportError('stripe', errorMessage, { component });
};

/**
 * Report UI error
 */
export const reportUIError = async (errorMessage, stackTrace = null, component = null) => {
  await reportError('ui', errorMessage, { stackTrace, component });
};

export default {
  detectPlatform,
  detectBrowser,
  reportError,
  updateSession,
  trackVoiceAttempt,
  trackSTTUsage,
  trackTTSUsage,
  reportSTTError,
  reportTTSError,
  reportStripeError,
  reportUIError
};
